import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  EmptyState,
  Filters,
  Input,
  LoadingState,
  Pagination,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createCommunitiesApi } from "../api";
import {
  communityStatusTone,
  formatLabel,
  healthStatusTone,
  visibilityStatusTone,
} from "../labels";
import type {
  CommunityDto,
  CommunitySummary,
  CommunityHealthStatus,
  CommunityStatus,
  VisibilityStatus,
} from "../types";
import { CreateCommunityDialog } from "../components/CommunityDialogs";

const STATUSES: CommunityStatus[] = [
  "draft",
  "active",
  "restricted",
  "paused",
  "archived",
  "closed",
];

const VISIBILITY: VisibilityStatus[] = [
  "visible",
  "hidden",
  "paused",
  "restricted",
];

const HEALTH: CommunityHealthStatus[] = [
  "healthy",
  "monitoring",
  "at_risk",
  "restricted",
  "critical",
];

const SORT_OPTIONS = [
  { value: "name", label: "Name (A–Z)" },
  { value: "-name", label: "Name (Z–A)" },
  { value: "status", label: "Status" },
  { value: "id", label: "ID" },
];

export function CommunitiesDirectoryPage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Communities" },
  ]);

  const client = useApiClient();
  const api = useMemo(() => createCommunitiesApi(client), [client]);
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") ?? "";
  const status = (searchParams.get("status") ?? "") as CommunityStatus | "";
  const visibilityStatus = (searchParams.get("visibility_status") ??
    "") as VisibilityStatus | "";
  const healthStatus = (searchParams.get("health_status") ??
    "") as CommunityHealthStatus | "";
  const marketId = searchParams.get("market_id") ?? "";
  const sort = searchParams.get("sort") ?? "name";
  const cursor = searchParams.get("cursor") ?? undefined;

  const [rows, setRows] = useState<CommunityDto[]>([]);
  // What Play stores about each row (members, owner, joining, reports). Loaded
  // after the page; a failure leaves the table usable and says so.
  const [summaries, setSummaries] = useState<Record<string, CommunitySummary>>({});
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchDraft, setSearchDraft] = useState(q);
  const [marketDraft, setMarketDraft] = useState(marketId);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const page = cursorStack.length + 1;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await api.list({
        ...(cursor ? { cursor } : {}),
        limit: 25,
        sort,
        ...(q ? { q } : {}),
        ...(status ? { status } : {}),
        ...(visibilityStatus ? { visibility_status: visibilityStatus } : {}),
        ...(healthStatus ? { health_status: healthStatus } : {}),
        ...(marketId ? { market_id: marketId } : {}),
      });
      setRows(list.communities);
      setNextCursor(list.pagination.next_cursor ?? undefined);
      setSummaryError(null);
      if (list.communities.length > 0) {
        try {
          const s = await api.summaries(list.communities.map((c) => c.id));
          setSummaries(s.summaries);
        } catch (err) {
          setSummaries({});
          setSummaryError(apiErrorMessage(err));
        }
      } else {
        setSummaries({});
      }
    } catch (err) {
      setError(apiErrorMessage(err));
      setRows([]);
      setNextCursor(undefined);
    } finally {
      setLoading(false);
    }
  }, [api, cursor, sort, q, status, visibilityStatus, healthStatus, marketId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchDraft(q);
    setMarketDraft(marketId);
  }, [q, marketId]);

  function updateParams(patch: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  }

  function applySearch() {
    setCursorStack([]);
    updateParams({
      q: searchDraft.trim() || undefined,
      market_id: marketDraft.trim() || undefined,
      cursor: undefined,
    });
  }

  function goNext() {
    if (!nextCursor) return;
    setCursorStack((stack) => [...stack, cursor ?? ""]);
    updateParams({ cursor: nextCursor });
  }

  function goPrevious() {
    if (cursorStack.length === 0) return;
    const stack = [...cursorStack];
    const previous = stack.pop();
    setCursorStack(stack);
    updateParams({
      cursor: previous && previous.length > 0 ? previous : undefined,
    });
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Communities"
        description="Govern Community lifecycle, membership, ownership, posts, health snapshots, and feature placement. Membership is independent of Ownership. Current health is a summary; snapshots are the history. Community Reports belong in Trust & Safety."
        actions={
          <Flex gap={8}>
            <Button variant="secondary" onClick={() => setCreateOpen(true)}>
              Create Community
            </Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      <Filters>
        <div style={{ flex: "1 1 240px", minWidth: 200 }}>
          <Search
            value={searchDraft}
            onValueChange={setSearchDraft}
            placeholder="Name or code (CM-…)"
            onKeyDown={(event) => {
              if (event.key === "Enter") applySearch();
            }}
          />
        </div>
        <Input
          label="Market ID"
          value={marketDraft}
          onChange={(event) => setMarketDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") applySearch();
          }}
        />
        <Select
          label="Status"
          value={status}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All statuses" },
            ...STATUSES.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Visibility"
          value={visibilityStatus}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              visibility_status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All visibility" },
            ...VISIBILITY.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Health"
          value={healthStatus}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({
              health_status: event.target.value || undefined,
              cursor: undefined,
            });
          }}
          options={[
            { value: "", label: "All health" },
            ...HEALTH.map((item) => ({
              value: item,
              label: formatLabel(item),
            })),
          ]}
        />
        <Select
          label="Sort"
          value={sort}
          onChange={(event) => {
            setCursorStack([]);
            updateParams({ sort: event.target.value, cursor: undefined });
          }}
          options={SORT_OPTIONS}
        />
        <Button variant="primary" onClick={applySearch}>
          Apply search
        </Button>
      </Filters>

      {error ? (
        <Alert tone="danger" title="Unable to load Communities">
          {error}
        </Alert>
      ) : null}

      {summaryError ? (
        <Alert tone="warning" title="Member counts and owners could not be loaded">
          {summaryError}
        </Alert>
      ) : null}

      {loading ? <LoadingState message="Loading Communities…" /> : null}

      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          reason={q || status || visibilityStatus || healthStatus || marketId ? "filters" : "none"}
          title="No Communities found"
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setCursorStack([]);
                setSearchParams(new URLSearchParams());
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : null}

      {!loading && rows.length > 0 ? (
        <Stack gap={12}>
          <ActionBar
            left={
              <Text role="small" color="secondary" as="span">
                Showing {rows.length} Communit{rows.length === 1 ? "y" : "ies"}
              </Text>
            }
            right={
              <Pagination
                page={page}
                pageCount={nextCursor ? page + 1 : page}
                onPageChange={(nextPage) => {
                  if (nextPage > page) goNext();
                  if (nextPage < page) goPrevious();
                }}
              />
            }
          />
          <Table>
            <Thead>
              <Tr>
                <Th>Community</Th>
                <Th>Code</Th>
                <Th>Owner</Th>
                <Th>Members</Th>
                <Th>Joining</Th>
                <Th>Reports</Th>
                <Th>Status</Th>
                <Th>Visibility</Th>
                <Th>Health</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => {
                const s = summaries[row.id];
                return (
                <Tr key={row.id}>
                  <Td>
                    <Text role="bodyStrong" as="div">
                      {row.name}
                    </Text>
                    <Text role="caption" color="muted" as="div">
                      {[s?.handle ? `@${s.handle}` : null, row.category].filter(Boolean).join(" · ") || "—"}
                    </Text>
                  </Td>
                  <Td>
                    <Text role="bodyStrong" as="span">
                      {row.display_code ?? "—"}
                    </Text>
                  </Td>
                  <Td>
                    {s ? (
                      s.source === "hq" ? (
                        <Text role="caption" color="muted" as="span">Created in HQ</Text>
                      ) : (
                        <>
                          <Text role="body" as="div">{s.owner_name ?? "—"}</Text>
                          {s.owner_handle ? (
                            <Text role="caption" color="muted" as="div">@{s.owner_handle}</Text>
                          ) : null}
                        </>
                      )
                    ) : "—"}
                  </Td>
                  <Td>
                    {s ? (
                      <>
                        <Text role="body" as="div">{s.member_count}</Text>
                        {s.admin_count > 0 ? (
                          <Text role="caption" color="muted" as="div">
                            {s.admin_count} admin{s.admin_count === 1 ? "" : "s"}
                          </Text>
                        ) : null}
                      </>
                    ) : "—"}
                  </Td>
                  <Td>
                    {s?.join_mode ? (
                      <>
                        <Text role="body" as="div">{s.join_mode === "approval" ? "Approval" : "Open"}</Text>
                        {s.pending_join_requests ? (
                          <Text role="caption" color="muted" as="div">{s.pending_join_requests} waiting</Text>
                        ) : null}
                      </>
                    ) : "—"}
                  </Td>
                  <Td>
                    {s ? (
                      s.open_reports > 0 ? (
                        <StatusChip tone="danger">{s.open_reports} open</StatusChip>
                      ) : (
                        <Text role="caption" color="muted" as="span">None</Text>
                      )
                    ) : "—"}
                  </Td>
                  <Td>
                    <StatusChip tone={communityStatusTone(row.status)}>
                      {formatLabel(row.status)}
                    </StatusChip>
                  </Td>
                  <Td>
                    <StatusChip tone={visibilityStatusTone(row.visibility_status)}>
                      {formatLabel(row.visibility_status)}
                    </StatusChip>
                  </Td>
                  <Td>
                    <StatusChip tone={healthStatusTone(row.health_status)}>
                      {row.health_status
                        ? formatLabel(row.health_status)
                        : "No snapshot"}
                    </StatusChip>
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => void navigate(`/app/communities/${row.id}`)}
                    >
                      Open
                    </Button>
                  </Td>
                </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Stack>
      ) : null}

      <CreateCommunityDialog
        open={createOpen}
        busy={createBusy}
        error={createError}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={async (input) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.create(input);
            toast.push({
              title: "Community created",
              description: result.community.name,
              tone: "success",
            });
            setCreateOpen(false);
            void navigate(`/app/communities/${result.community.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />
    </Stack>
  );
}
